import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';

import {
  ChakraBox,
  FAIconName,
  FireflyTertiaryButtonMenu,
  FireflyTertiaryButtonMenuItem,
  FlagIconInput,
  TitleInput,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import {
  fillActivityWith,
  NoteFormDefaultSessionLink,
} from '~/components/maud/patients/note/editNoteForm.util';
import { noteTemplateRevisionsToDropdownOptions } from '~/components/maud/patients/note/noteTemplateRevisionsToDropdownOptions';
import { NoteCommunicationMethod } from '~/lib/constants/AddPatientNoteModal.constants';
import { NO_NOTE_TEMPLATE_OPTION } from '~/lib/constants/notes';
import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { getNoteTypeBasedOnAppointmentType } from '~/lib/util/notes/notes.util';
import { AppointmentTypeName } from '~/lib/constants/scheduling.constants';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { useFormikContext } from 'formik';
import { useNoteFormSessionListOptions } from '~/lib/hooks/useNoteFormSessionListOptions';
import util from '~/components/common/calendar/util';
import { useGetAppointmentDetailsByIdQuery } from '~/store/services/notes/notes';

type Props = {
  id: string;
  activityWithOptions: NoteMultiSelectOption[];
  clickedTemplateId: string | null;
  setClickedTemplateId: Dispatch<SetStateAction<string | null>>;
  isPatientAppointmentsLoading: boolean;
  isRevisionDataLoading: boolean;
  noteTemplateRevisions: NoteTemplateRevision[];
  onFormChange: () => void;
  patientAppointmentsData: GetAppointmentDetailsResponse;
  patientAppointmentsError: string;
  setIsTemplateSwitching: Dispatch<SetStateAction<boolean>>;
};

export const EditPatientNoteFormTitleRow = ({
  id,
  activityWithOptions,
  clickedTemplateId,
  setClickedTemplateId,
  isPatientAppointmentsLoading,
  isRevisionDataLoading,
  noteTemplateRevisions,
  onFormChange,
  patientAppointmentsData,
  patientAppointmentsError,
  setIsTemplateSwitching,
}: Props) => {
  const {
    values: formValues,
    setFieldValue: setFormFieldBypassingUnsavedChangesHandling,
  } = useFormikContext<EditNoteFormData>();
  const { setFormField, isLoading, fieldErrors, setFieldErrors } =
    useEditPatientNoteFormContext();

  const { data: patientAppointmentDetailsByIdData } =
    useGetAppointmentDetailsByIdQuery(formValues.appointment?.node, {
      skip: !formValues.appointment?.node,
    });

  const { sessionDetailsListOptions } = useNoteFormSessionListOptions({
    patientAppointments: patientAppointmentsData,
    selectedAppointmentDetails: patientAppointmentDetailsByIdData,
    selectedAppointmentId: formValues.appointment?.node,
  });

  // Finding a match with note.appointmentExternalId in appointments
  // and update the menu option value object
  useEffect(() => {
    if (sessionDetailsListOptions.length > 1) {
      const appointmentValue: FireflyTertiaryButtonMenuItem =
        formValues.appointment;
      if (appointmentValue) {
        const matchedAppointment = sessionDetailsListOptions.find(
          (appointmentDetail) =>
            appointmentDetail.node === appointmentValue.node,
        );
        if (matchedAppointment) {
          setFormFieldBypassingUnsavedChangesHandling(
            'appointment',
            matchedAppointment,
          );
        } else if (patientAppointmentDetailsByIdData) {
          const apptMenuItem = util.convertAppointmentDataToButtonMenuItem(
            patientAppointmentDetailsByIdData,
          );
          setFormFieldBypassingUnsavedChangesHandling(
            'appointment',
            apptMenuItem,
          );
        }
      }
    }
  }, [
    sessionDetailsListOptions,
    formValues?.appointment,
    patientAppointmentDetailsByIdData,
  ]);

  const hasNoAppointmentsData = useMemo(() => {
    const sessionDetailsListSansDefault = sessionDetailsListOptions.filter(
      (item) =>
        item.node !== NoteFormDefaultSessionLink.node &&
        item.label !== NoteFormDefaultSessionLink.label,
    );

    return (
      (isEmpty(sessionDetailsListSansDefault) ||
        !isEmpty(patientAppointmentsError)) &&
      !isPatientAppointmentsLoading
    );
  }, [
    sessionDetailsListOptions,
    patientAppointmentsError,
    isPatientAppointmentsLoading,
  ]);

  return (
    <ChakraBox
      alignItems="center"
      display="flex"
      gap="24px"
      height="25px"
      marginBottom="12px"
    >
      <ChakraBox alignItems="center" display="flex" gap="12px" height="25px">
        <TitleInput
          id={`${id}__title`}
          isInvalid={Boolean(fieldErrors.titleError)}
          onChange={(val) => {
            setFormField('title', val);
            setFieldErrors((prev) => ({ ...prev, titleError: null }));
          }}
          placeholder="Enter Title"
          value={formValues.title || ''} // this || '' is necessary to ensure an old value that gets cleared is properly set to empty
        />
        <FlagIconInput
          iconAriaLabel="Flag as confidential or sensitive"
          id={`${id}__is-confidential`}
          isDisabled={isLoading}
          isSelected={formValues.isConfidential}
          onClick={() => {
            setFormField('isConfidential', !formValues.isConfidential);
            onFormChange();
          }}
          tooltipDescriptionText="Flag as confidential or sensitive"
        />
      </ChakraBox>
      <FireflyTertiaryButtonMenu
        aria-label="appointment"
        buttonId={`${id}__appointment`}
        buttonLeftIcon={FAIconName.linkRegular}
        buttonText={
          formValues.appointment?.label || NoteFormDefaultSessionLink.node
        }
        isDisabled={
          isLoading || isPatientAppointmentsLoading || hasNoAppointmentsData
        }
        isLoading={isPatientAppointmentsLoading}
        onOptionClick={(value) => {
          const selectedOption =
            value !== NoteFormDefaultSessionLink.node
              ? sessionDetailsListOptions.find((item) => item.node === value)
              : undefined;

          setFormField('appointment', selectedOption);

          // Fill the other fields with appointment details
          const appointmentDetails = patientAppointmentsData.data.find(
            (e) => e.appointmentExternalId === value,
          );

          if (appointmentDetails) {
            setFormField('title', appointmentDetails.appointmentTypeLabel);
            setFormField(
              'activityDate',
              new Date(appointmentDetails.appointmentStartDateTime),
            );

            const activityWith = fillActivityWith(
              appointmentDetails,
              activityWithOptions,
            );
            if (!isEmpty(activityWith)) {
              setFormField('activity', activityWith);
            }

            const noteType = getNoteTypeBasedOnAppointmentType(
              appointmentDetails?.appointmentType as AppointmentTypeName,
            );
            if (noteType) {
              setFormField('type', {
                label: noteType,
                value: noteType,
              });
            }

            setFormField('medium', {
              label: NoteCommunicationMethod.VIDEO,
              value: NoteCommunicationMethod.VIDEO,
            });
          }

          onFormChange();

          if (value) {
            setFieldErrors((prev) => ({
              ...prev,
              typeError: null,
              dateError: null,
              titleError: null,
            }));
          }
        }}
        options={sessionDetailsListOptions}
      />
      <FireflyTertiaryButtonMenu
        buttonId={`${id}__template`}
        buttonLeftIcon={FAIconName.fileLinesRegular}
        buttonText={
          formValues.noteTemplateRevision?.label ||
          NO_NOTE_TEMPLATE_OPTION.label
        }
        isDisabled={false}
        isLoading={isRevisionDataLoading}
        onOptionClick={(templateId) => {
          if (clickedTemplateId === templateId) return;

          setIsTemplateSwitching(true);
          setClickedTemplateId(templateId);
        }}
        options={noteTemplateRevisionsToDropdownOptions(noteTemplateRevisions)}
      />
    </ChakraBox>
  );
};
